import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import AppCell from '@/components/common/app-cell';
import ScrollSelector from '@/components/common/ScrollSelector';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LanguageSelect, { LanguageSelectPage } from '@/components/mine/language-select';
import useUserPlatform, { type DisplayMode, useDisplayMode } from '@/hooks/use-user-platform';
import { supportedLanguages } from '@/i18n';
import ROUTES from '@/routes';
import { logMessage } from '@/services/log-message';
import { useMessageStore } from '@/stores/message';
import { makePrompt } from '@/stores/prompter';

export default function GeneralSettingsIndexRoute() {
  const [showLang, setShowLang] = useState(false);
  const queryClient = useQueryClient();
  const [showDisplay, setShowDisplay] = useState(false);
  const { t, i18n } = useTranslation();
  const { isEmbedded } = useUserPlatform();
  const [displayMode, setDisplayMode] = useDisplayMode();

  const handleClearStorage = () =>
    makePrompt({
      key: 'clear-storage',
      type: 'dialog',
      title: t('messaging-settings.trim-storage.title'),
      content: t('messaging-settings.trim-storage.description'),
      promise: async () => {
        logMessage('CLEAR_CACHE');
        queryClient.clear();
        await useMessageStore.getState().trim();
      },
    });

  if (displayMode === 'desktop' && showLang) {
    return <LanguageSelectPage onBack={() => setShowLang(false)} />;
  }
  return (
    <PageLayout
      title={t('mine.general')}
      headerConfig={{
        navigateBack: displayMode === 'mobile',
      }}>
      <div className='desktop:mx-1 flex flex-col'>
        {!isEmbedded && (
          <AppCell
            label={t('general-settings.language')}
            content={
              supportedLanguages.find(({ value }) => i18n.resolvedLanguage === value)?.label ??
              t('general-settings.system-language')
            }
            chevron
            onClick={() => setShowLang(true)}
          />
        )}

        {!isEmbedded && (
          <AppCell
            label={t('general-settings.display-mode.title')}
            content={
              displayMode === 'desktop'
                ? t('general-settings.display-mode.desktop')
                : t('general-settings.display-mode.mobile')
            }
            chevron
            onClick={() => setShowDisplay(true)}
          />
        )}

        <Link to={ROUTES.MY.SETTINGS.GENERAL.FEEDBACK.$path()}>
          <AppCell
            hoverable
            label={t('general-settings.feedback')}
            chevron
          />
        </Link>

        {!isEmbedded && (
          <Link to={ROUTES.MY.SETTINGS.GENERAL.ABOUT_US.$path()}>
            <AppCell
              hoverable
              label={t('general-settings.about-us')}
              chevron
            />
          </Link>
        )}

        <AppCell
          label={t('messaging-settings.clear-cache')}
          onClick={handleClearStorage}
          chevron
        />

        <Link to={ROUTES.MY.SETTINGS.GENERAL.DEBUG.$path()}>
          <AppCell
            hoverable
            label={t('general-settings.debug.title')}
            chevron
          />
        </Link>
      </div>

      {!isEmbedded && (
        <LanguageSelect
          show={showLang}
          onClose={() => setShowLang(false)}
        />
      )}

      {!isEmbedded && (
        <ScrollSelector
          show={showDisplay}
          onCancel={() => setShowDisplay(false)}
          onConfirm={(value) => {
            setDisplayMode(value as DisplayMode);
            setShowDisplay(false);
          }}
          title={t('general-settings.display-mode.title')}
          defaultValue={displayMode}
          items={['desktop', 'mobile'].map((value) => ({
            key: value,
            label: t(`general-settings.display-mode.${value}`),
            value,
          }))}
        />
      )}
    </PageLayout>
  );
}
